import React, { ChangeEvent, useState } from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { Component } from './Component/Component.component';
import { CategoryHeader } from './CategoryHeader.component';
import { CategoryComponentsHeader } from './CategoryComponentsHeader.component';
import { CategoryFooter } from './CategoryFooter.component';
import { ClassicCategoryHeader } from './ClassicCategoryHeader.component';
import { ClassicCategoryBody } from './ClassicCategoryBody.component';
import { selectCofStore, selectLofStore, selectRiskStore } from '@Selectors';
import { selectUnrecognisedValuesForSection } from '@Selectors/RiskTemplateApply.selectors';
import { selectMissingSourceKind } from '@Selectors/ComponentSource.selectors';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import {
	deleteCategory,
	deleteCoFCategory,
	setCoFComponents,
	setCoFConfig,
	setLoFComponents,
	setLoFConfig,
} from '@Actions';
import { useDispatch, useSelector } from 'react-redux';
import { InfoCard } from '@innovyze/stylovyze';
import {
	Category as CategoryType,
	CategoryProps,
	Component as ComponentType,
} from '@Types';
import { useStyle } from './Category.styles';
import { WarningDialog } from '@innovyze/stylovyze';
import Scales from '@Components/Utils/icons/Scales';
import { useGlobalization } from '@Translations/useGlobalization';
import { NamespacedStoreState } from '@Types/store.types';
import { useNoConditionData } from '@Hooks/useNoConditionData';
import { distributeToHundred } from '@Utils/distributeToHundred';
import { countComponentValuesNeedingScore } from '@Components/RiskSetup/templates/countValuesNeedingScore';
import { useComponentCoverage } from './useComponentCoverage';

// Every component slider uses the same colour, regardless of category —
// only the category-level slider (CATEGORY_ACCENT in CategoryHeader) varies.
const COMPONENT_SLIDER_COLOR = '#1976d2';

// Resolves which category (and its components) this instance is showing,
// out of whichever store applies — LoF or CoF. Its own function so this
// lookup's branching doesn't count against Category's own complexity.
const resolveCategory = (
	forLoF: boolean,
	LoFCategoryIndex: number | undefined,
	lofStore: ReturnType<typeof selectLofStore> | null,
	cofStore: ReturnType<typeof selectCofStore> | null,
): {
	category: CategoryType | undefined;
	components: ComponentType[] | undefined;
} => {
	const store = forLoF ? lofStore : cofStore;
	if (
		LoFCategoryIndex === undefined ||
		store == undefined ||
		(store.categories?.length ?? 0) <= 0
	) {
		return { category: undefined, components: undefined };
	}
	const category = store.categories[LoFCategoryIndex];
	return { category, components: category?.components };
};

// Renders the card border via a descendant-selector Box only when redesigned
// — a plain Fragment when not, so the classic (flag-off) tree gains no extra
// DOM node and stays byte-for-byte identical to its pre-redesign markup.
const CategoryCardWrapper = ({
	redesigned,
	children,
}: {
	redesigned: boolean;
	children: React.ReactNode;
}) =>
	redesigned ? (
		<Box
			sx={{
				'& .MuiCard-root, & .MuiPaper-root': {
					border: '1px solid #eceef2',
					borderLeft: '3px solid #3b8fd4',
					borderRadius: '6px',
					boxShadow:
						'0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.05)',
				},
			}}>
			{children}
		</Box>
	) : (
		<>{children}</>
	);

export const Category = ({
	isLoading,
	forLoF,
	LoFCategoryIndex,
	isWeightingInvalid = false,
	canBeDiscarded = true,
	onCategoryWeightingChanged,
	onAddComponent,
	onEditComponent,
}: CategoryProps) => {
	const { t } = useGlobalization();
	const classes = useStyle();
	const dispatch = useDispatch();
	const missingSourceKind = useSelector(selectMissingSourceKind);
	const unrecognisedValues = useSelector((state: NamespacedStoreState) =>
		selectUnrecognisedValuesForSection(state, forLoF ? 'lof' : 'cof'),
	);
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();

	const [open, setOpen] = useState(false);

	const riskStore = selectRiskStore();
	const lofStore = forLoF ? selectLofStore() : null;
	const cofStore = !forLoF ? selectCofStore() : null;

	const { category, components } = resolveCategory(
		forLoF,
		LoFCategoryIndex,
		lofStore,
		cofStore,
	);

	// The category as last loaded from the server (not the user's unsaved edits).
	const savedCategory = (
		forLoF
			? riskStore.riskConfig.LOFCategories
			: riskStore.riskConfig.COFCategories
	)?.find(c => c.name === category?.name);
	const coverageFor = useComponentCoverage(
		category,
		isRiskTemplatesEnabled,
		savedCategory,
	);

	const handleUseExclusivelyChange = (
		event: ChangeEvent<HTMLInputElement>,
	) => {
		if (lofStore) {
			dispatch(
				setLoFConfig(
					lofStore.categories.map((cat, index) =>
						index === LoFCategoryIndex
							? {
									...cat,
									useThisOnly: event.target.checked,
									weighting: event.target.checked
										? 100
										: cat.weighting,
							  }
							: cat,
					),
				),
			);
		}
	};

	const handleUseHighestChange = (checked: boolean) => {
		if (!forLoF) {
			if (cofStore) {
				dispatch(
					setCoFConfig(
						cofStore.categories.map((cat, index) =>
							index === LoFCategoryIndex
								? {
										...cat,
										useHighestScore: checked,
								  }
								: cat,
						),
					),
				);
			}
		} else {
			if (lofStore) {
				dispatch(
					setLoFConfig(
						lofStore.categories.map((cat, index) =>
							index === LoFCategoryIndex
								? {
										...cat,
										useHighestScore: checked,
								  }
								: cat,
						),
					),
				);
			}
		}
	};

	const onDeleteComponent = (title: string) => {
		if (category && components) {
			if (forLoF) {
				dispatch(
					setLoFComponents({
						categoryName: category.name,
						components: components.filter(
							component => component.name !== title,
						),
					}),
				);
			} else {
				dispatch(
					setCoFComponents({
						categoryName: category.name,
						components: components.filter(
							component => component.name !== title,
						),
					}),
				);
			}
		}
	};

	const isInvalid = (): boolean => {
		const value = components
			?.filter(comp => comp.select)
			?.map(comp => comp.weighting)
			?.reduce((pre, current) => pre + current, 0);

		return value !== undefined && value > 0 && value !== 100;
	};

	// Apply a full new weight array (same order as the selected components)
	// in one dispatch, used by balanceComponents to rescale everyone at once.
	const applyComponentWeights = (
		selected: ComponentType[],
		values: number[],
	) => {
		if (!category || !components) return;
		const weightByName = new Map<string, number>(
			selected.map((c, i) => [c.name, values[i]]),
		);
		const newComps = components.map(c =>
			weightByName.has(c.name)
				? {
						...c,
						weighting: weightByName.get(c.name) as number,
						select: true,
				  }
				: c,
		);
		const newCat = { ...category, components: newComps };
		if (lofStore) {
			dispatch(
				setLoFConfig(
					lofStore.categories.map(c =>
						c.name === newCat.name ? newCat : c,
					),
				),
			);
		} else if (cofStore) {
			dispatch(
				setCoFConfig(
					cofStore.categories.map(c =>
						c.name === newCat.name ? newCat : c,
					),
				),
			);
		}
	};

	// One click to reach 100%: scale the switched-on components proportionally,
	// keeping the ratios the user set. Only touches components that are
	// already selected — it must never re-enable one the user deliberately
	// switched off. If nothing is selected there's nothing to rescale;
	// CategoryComponentsHeader hides the action in that case.
	const balanceComponents = () => {
		if (!category || !components) return;
		const targets = components.filter(c => c.select);
		if (targets.length === 0) return;
		const rounded = distributeToHundred(targets.map(c => c.weighting || 0));
		applyComponentWeights(targets, rounded);
	};

	// Same check as the Score Setup source pickers: a saved component whose
	// source isn't in the tenant's data shows the "Component to add" chip.
	const missingKindFor = (comp: ComponentType) =>
		isRiskTemplatesEnabled && category
			? missingSourceKind(forLoF, !!category.isCondition, comp)
			: undefined;

	const allComponents = () => {
		// One lookup per row (missing and missingKind both need it).
		const missingKinds = new Map(
			components?.map(comp => [comp.name, missingKindFor(comp)]),
		);
		return components?.map((comp, index) => (
			<Component
				key={comp.name}
				title={comp.name}
				value={comp.weighting}
				enabledChecked={comp.select}
				invalid={isInvalid()}
				disableSlider={category?.weighting === 0}
				checkBox={category?.useHighestScore}
				redesigned={isRiskTemplatesEnabled}
				accent={COMPONENT_SLIDER_COLOR}
				missing={missingKinds.get(comp.name) !== undefined}
				missingKind={missingKinds.get(comp.name)}
				needsScoreCount={
					isRiskTemplatesEnabled
						? countComponentValuesNeedingScore(
								comp,
								unrecognisedValues,
						  )
						: 0
				}
				categoryName={category?.name}
				{...coverageFor(comp)}
				onSliderValueChanged={(value: number) => {
					if (lofStore && components && category) {
						const newCat = {
							...category,
							components: components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: value,
											select: value !== 0,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setLoFConfig(
								lofStore.categories.map(category =>
									category.name === newCat.name
										? newCat
										: category,
								),
							),
						);
					} else if (cofStore && category && components) {
						const newCat = {
							...category,
							components: components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: value,
											select: value !== 0,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setCoFConfig(
								cofStore.categories.map(cof =>
									cof.name === newCat.name ? newCat : cof,
								),
							),
						);
					}
				}}
				onSelectChecked={(checked: boolean) => {
					if (lofStore && category) {
						const newCat = {
							...category,
							components: category.components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: 0,
											select: checked,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setLoFConfig(
								lofStore.categories.map(category =>
									category.name === newCat.name
										? newCat
										: category,
								),
							),
						);
					} else if (cofStore && components && category) {
						const newCat = {
							...category,
							components: category.components.map(
								(onChangeComp, onChangeIndex) => {
									if (onChangeIndex === index) {
										return {
											...onChangeComp,
											weighting: 0,
											select: checked,
										};
									} else {
										return onChangeComp;
									}
								},
							),
						};
						dispatch(
							setCoFConfig(
								cofStore.categories.map(cat =>
									cat.name === newCat.name ? newCat : cat,
								),
							),
						);
					}
				}}
				onEdit={title => {
					if (components) {
						const targetComponent = components.find(
							o => o.name == title,
						);
						if (targetComponent && onEditComponent) {
							onEditComponent(forLoF, category, {
								...targetComponent,
							});
						}
					}
				}}
				onDelete={onDeleteComponent}
			/>
		));
	};

	const ifLoadingTrue = (
		<Grid
			container
			item
			xs={12}
			justifyContent="center"
			style={{ paddingTop: '20px', paddingBottom: '20px' }}>
			<CircularProgress />
		</Grid>
	);
	const ifLoadingFalse =
		components && components.length > 0 ? (
			allComponents()
		) : (
			<Grid container>
				<Grid
					container
					item
					xs={12}
					justifyContent="center"
					style={{ paddingTop: '40px' }}>
					<Scales />
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<Typography variant="h6">
						{t('You do not have any components added')}
					</Typography>
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<Typography variant="caption">
						{t('Add components to set up weighting.')}
					</Typography>
				</Grid>
				<Grid
					container
					item
					xs={12}
					justifyContent="center"
					style={{
						paddingTop: '20px',
						paddingBottom: '20px',
					}}>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => onAddComponent?.(forLoF, category)}>
						{t('Add Component')}
					</Button>
				</Grid>
			</Grid>
		);

	const noConditionData = useNoConditionData(
		riskStore.riskConfig.riskSetting.assetType,
	);

	const getCatWeightingDesc = () => {
		if (forLoF && !noConditionData) {
			return category?.isCondition
				? t('Uncheck box above to adjust category weighting')
				: t('When there is no condition data');
		} else {
			return '';
		}
	};

	return (
		<div>
			<WarningDialog
				open={open}
				title={t('Discard category?')}
				content={t('Are you sure you want to discard this category?')}
				cancelText={t('Cancel')}
				confirmText={t('Confirm')}
				onCancel={() => setOpen(false)}
				onConfirm={() =>
					dispatch(
						forLoF
							? deleteCategory({
									forLoF: forLoF,
									categoryName: category?.name ?? '',
							  })
							: deleteCoFCategory({
									forLoF: forLoF,
									categoryName: category?.name ?? '',
							  }),
					)
				}
			/>
			<CategoryCardWrapper redesigned={isRiskTemplatesEnabled}>
				<InfoCard
					title=""
					className={isLoading ? 'wait' : ''}
					titleArea={
						isRiskTemplatesEnabled ? (
							<CategoryHeader
								category={category}
								forLoF={forLoF}
								isLoading={isLoading}
								isWeightingInvalid={isWeightingInvalid}
								onCategoryWeightingChanged={
									onCategoryWeightingChanged
								}
								onUseExclusivelyChange={
									handleUseExclusivelyChange
								}
							/>
						) : (
							<ClassicCategoryHeader
								category={category}
								forLoF={forLoF}
								isLoading={isLoading}
								canBeDiscarded={canBeDiscarded}
								vlClassName={classes.vl}
								onUseExclusivelyChange={
									handleUseExclusivelyChange
								}
								onDiscardClick={() => setOpen(true)}
								onAddComponent={onAddComponent}
							/>
						)
					}>
					{isRiskTemplatesEnabled ? (
						<CategoryComponentsHeader
							components={components}
							useHighestScore={category?.useHighestScore}
							categoryWeighting={category?.weighting}
							isLoading={isLoading}
							onBalance={balanceComponents}
							onUseHighestChange={handleUseHighestChange}
						/>
					) : (
						<ClassicCategoryBody
							category={category}
							isLoading={isLoading}
							isWeightingInvalid={isWeightingInvalid}
							weightingDescription={getCatWeightingDesc()}
							columnHeaderClassName={classes.columnHeaderBreaker}
							onCategoryWeightingChanged={
								onCategoryWeightingChanged
							}
							onUseHighestChange={handleUseHighestChange}
						/>
					)}

					{isLoading ? ifLoadingTrue : ifLoadingFalse}

					{isRiskTemplatesEnabled && (
						<CategoryFooter
							category={category}
							components={components}
							forLoF={forLoF}
							isLoading={isLoading}
							canBeDiscarded={canBeDiscarded}
							onAddComponent={onAddComponent}
							onDiscardClick={() => setOpen(true)}
						/>
					)}

					<hr className={classes.divider} />
				</InfoCard>
			</CategoryCardWrapper>
		</div>
	);
};
